import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Image as ImageIcon, Upload, ScanLine, ChevronLeft, CheckCircle2, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, fmtCHF, fmtDate, formatApiError } from "@/lib/api";
import { prepareImage } from "@/lib/image";
import { DOC_STATUS, TRUST, PAYMENT_LABELS, ORGAN_LABELS } from "@/lib/organs";
import { SectionTitle } from "@/components/Shared";
import PipelineStepper from "@/components/PipelineStepper";
import ResolveDialog from "@/components/ResolveDialog";

const STEPS = ["Separazione documento", "Lettura AI", "Identificazione azienda", "Verifica duplicati", "Controllo regole e storico", "Collegamento agli organi", "Archiviazione"];

export default function Scan() {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const fileRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | processing | result
  const [step, setStep] = useState(0);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [resolving, setResolving] = useState(null);

  const process = async (file) => {
    if (!file) return;
    setPhase("processing");
    setStep(0);
    setPreview(null);
    let docId;
    try {
      const prepared = await prepareImage(file);
      setPreview(prepared.dataUrl);
      // Acquisizione immediata e durevole: la risposta non attende l'OCR
      const { data } = await api.post("/scan", {
        image_base64: prepared.dataUrl.split(",")[1],
        filename: prepared.filename || "scansione.jpg",
        channel: "scan",
        client_preprocessed: prepared.preprocessed,
      });
      docId = data.document.doc_id;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Acquisizione non riuscita. Riprova.");
      setPhase("idle");
      return;
    }
    // L'elaborazione continua server-side: questa pagina può essere chiusa in qualunque momento.
    for (let i = 0; i < 45; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
      try {
        const { data: d } = await api.get(`/documents/${docId}`);
        if (d.status === "failed") {
          setResult({ document: d });
          setPhase("failed");
          return;
        }
        if (d.status !== "processing") {
          setResult({ document: d });
          setPhase("result");
          return;
        }
      } catch {}
    }
    setResult({ document: null });
    setPhase("result");
  };

  const confirm = async () => {
    if (!result?.document) return;
    await api.post(`/documents/${result.document.doc_id}/confirm`).catch(() => {});
    setConfirmed(true);
    toast.success("Documento confermato e archiviato nella Memoria aziendale.");
  };

  const reset = () => { setPhase("idle"); setResult(null); setPreview(null); setConfirmed(false); };

  const doc = result?.document;
  const dst = doc ? DOC_STATUS[doc.status] : null;

  return (
    <div className="max-w-2xl mx-auto px-4" data-testid="scan-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-300 mb-4"><ChevronLeft size={14} /> Organismo</Link>
      <div className="mb-6 fade-up">
        <p className="text-xs tracking-[0.25em] text-gray-500">OCCHI & ORECCHIE · ACQUISIZIONE</p>
        <h1 className="font-display text-3xl font-bold mt-1">Scansiona</h1>
        <p className="text-sm text-gray-500 mt-1">Fotografa un documento. Il sistema lo legge, lo verifica, lo collega agli organi e lo archivia.</p>
      </div>

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="scan-input-camera" onChange={(e) => process(e.target.files[0])} />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" data-testid="scan-input-gallery" onChange={(e) => process(e.target.files[0])} />
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="scan-input-file" onChange={(e) => process(e.target.files[0])} />

      {phase === "idle" && (
        <div className="space-y-4 fade-up">
          <button data-testid="scan-btn-camera" onClick={() => cameraRef.current.click()}
            className="w-full glass rounded-3xl p-8 flex flex-col items-center gap-3 scan-glow hover:border-cyan-400/40 transition-colors">
            <span className="w-16 h-16 rounded-full flex items-center justify-center text-[#030712]"
              style={{ background: "linear-gradient(135deg,#00E5FF,#3B82F6)" }}>
              <Camera size={28} />
            </span>
            <span className="font-display font-bold text-xl tracking-wide">SCANSIONA</span>
            <span className="text-xs text-gray-500">Usa la fotocamera dello smartphone</span>
          </button>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="scan-btn-gallery" onClick={() => galleryRef.current.click()} className="btn-ghost !py-4"><ImageIcon size={16} /> Galleria</button>
            <button data-testid="scan-btn-upload" onClick={() => fileRef.current.click()} className="btn-ghost !py-4"><Upload size={16} /> Carica file</button>
          </div>
          <Link to="/scan-bulk" data-testid="scan-btn-bulk" className="glass rounded-3xl p-5 flex items-center justify-between hover:border-cyan-400/30 transition-colors">
            <div className="flex items-center gap-3">
              <ScanLine size={20} className="text-cyan-300" />
              <div>
                <div className="text-sm font-semibold">Bulk Scan</div>
                <div className="text-xs text-gray-500">Fotografa in sequenza fino a 30 documenti accumulati</div>
              </div>
            </div>
            <span className="text-cyan-300 text-xs font-semibold">AVVIA →</span>
          </Link>
        </div>
      )}

      {phase === "processing" && (
        <div className="glass rounded-3xl p-8 fade-up" data-testid="scan-processing">
          <div className="flex items-center gap-5">
            {preview && <img src={preview} alt="Documento" className="w-20 h-24 object-cover rounded-2xl border border-cyan-400/20" />}
            <div className="flex-1">
              <p className="text-sm font-semibold mb-1">Documento acquisito e al sicuro.</p>
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                Il sistema sta elaborando in background: puoi lasciare questa pagina in qualunque momento.
                Il documento è già visibile in <Link to="/archive" className="text-cyan-300">Archivio</Link> e
                in <Link to="/queue" className="text-cyan-300">Coda</Link> come «In elaborazione».
              </p>
              <div className="space-y-1.5">
                {STEPS.map((s, i) => (
                  <div key={s} data-testid={`scan-step-${i}`} className={`text-xs flex items-center gap-2 transition-opacity ${i <= step ? "text-cyan-200" : "text-gray-600"}`}>
                    {i < step ? <CheckCircle2 size={12} className="text-emerald-400" /> : <span className={`w-3 h-3 rounded-full border ${i === step ? "border-cyan-300 animate-pulse" : "border-gray-700"}`} />}
                    {s}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="glass rounded-3xl p-8 text-center fade-up" data-testid="scan-failed">
          <p className="text-sm text-red-300 font-medium">Elaborazione fallita</p>
          <p className="text-xs text-gray-500 mt-2">{result?.document?.note || "Errore durante la lettura del documento."}</p>
          <p className="text-[11px] text-gray-600 mt-2">L'originale è al sicuro. Gli altri documenti non sono stati influenzati.</p>
          <div className="flex gap-2 justify-center mt-4">
            <Link to="/queue" data-testid="scan-failed-queue" className="btn-primary">Apri Coda e riprova</Link>
            <button data-testid="scan-failed-new" onClick={reset} className="btn-ghost">Nuova scansione</button>
          </div>
        </div>
      )}

      {phase === "result" && !doc && (
        <div className="glass rounded-3xl p-8 text-center fade-up" data-testid="scan-still-processing">
          <p className="text-sm text-gray-300">Elaborazione ancora in corso.</p>
          <p className="text-xs text-gray-500 mt-2">Il documento è al sicuro: segui lo stato in Coda o in Archivio.</p>
          <Link to="/queue" data-testid="scan-goto-queue" className="btn-primary inline-flex mt-4">Vai alla Coda</Link>
        </div>
      )}

      {phase === "result" && doc && (
        <div className="space-y-4 fade-up" data-testid="scan-result">
          <div className="glass rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <span className="chip" style={{ color: dst.color, borderColor: `${dst.color}40`, background: `${dst.color}12` }}>{dst.label}</span>
              <span className="chip" style={{ color: TRUST[doc.trust]?.color }}>{TRUST[doc.trust]?.label}</span>
              {doc.learned && <span className="chip !text-cyan-200"><Sparkles size={11} /> Regola appresa applicata</span>}
            </div>
            <div className="flex gap-5">
              {preview && <img src={preview} alt="Documento" className="w-24 h-32 object-cover rounded-2xl border border-cyan-400/20 shrink-0" />}
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 flex-1 text-sm">
                <div><div className="text-[10px] text-gray-600 uppercase tracking-widest">Tipo</div><div data-testid="scan-result-type">{doc.doc_type}</div></div>
                <div><div className="text-[10px] text-gray-600 uppercase tracking-widest">Emittente</div><div data-testid="scan-result-supplier">{doc.supplier || "—"}</div></div>
                <div><div className="text-[10px] text-gray-600 uppercase tracking-widest">Data</div><div>{fmtDate(doc.date)}</div></div>
                <div><div className="text-[10px] text-gray-600 uppercase tracking-widest">Importo</div><div data-testid="scan-result-amount" className="font-mono">{fmtCHF(doc.amount)}</div></div>
                <div><div className="text-[10px] text-gray-600 uppercase tracking-widest">IVA</div><div className="font-mono">{fmtCHF(doc.vat)}</div></div>
                <div><div className="text-[10px] text-gray-600 uppercase tracking-widest">Categoria proposta</div><div data-testid="scan-result-category">{doc.category || "—"}</div></div>
                <div><div className="text-[10px] text-gray-600 uppercase tracking-widest">Metodo pagamento</div><div data-testid="scan-result-payment">{PAYMENT_LABELS[doc.payment_method] || doc.payment_method || "—"}</div></div>
              </div>
            </div>
            {doc.note && (
              <div className="mt-4 text-xs rounded-xl px-4 py-2.5"
                style={{ color: doc.status === "anomaly" ? "#EF4444" : "#F59E0B", background: doc.status === "anomaly" ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)" }}
                data-testid="scan-result-note">{doc.note}</div>
            )}
            <div className="mt-4">
              <SectionTitle>Organi collegati · un solo originale</SectionTitle>
              <div className="flex flex-wrap gap-1.5">
                {(doc.organs || []).map((o) => <span key={o} className="chip !text-[10px]">{ORGAN_LABELS[o] || o}</span>)}
              </div>
            </div>
            {doc.pipeline && (
              <div className="mt-4">
                <SectionTitle>Pipeline canonica</SectionTitle>
                <PipelineStepper pipeline={doc.pipeline} detailed />
              </div>
            )}
            {doc.storage_ref?.checksum && (
              <div className="mt-4 text-[11px] text-gray-500 font-mono" data-testid="scan-storage-ref">
                Originale immutabile · {doc.storage_ref.storage_provider === "internal_dev" ? "Storage interno (sviluppo)" : doc.storage_ref.storage_provider}
                {" · sha256 "}{doc.storage_ref.checksum.slice(0, 16)}… · v{doc.storage_ref.version}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {doc.status === "ready" && (
              <button data-testid="scan-confirm-btn" onClick={confirm} disabled={confirmed}
                className="btn-primary !py-3.5">
                {confirmed ? <><CheckCircle2 size={16} /> Archiviato</> : "Conferma e archivia"}
              </button>
            )}
            {["to_confirm", "anomaly"].includes(doc.status) && (
              <button data-testid="scan-resolve-btn" onClick={() => setResolving(doc)}
                className="btn-primary !py-3.5">
                {(doc.pending_fields || []).some((p) => p.field === "payment_method") ? "Indica metodo di pagamento" : "Completa verifica"}
              </button>
            )}
            {doc.status === "archived" && (
              <div className="btn-primary !py-3.5 opacity-80"><CheckCircle2 size={16} /> Verificato dal sistema</div>
            )}
            <button data-testid="scan-new-btn" onClick={reset} className="btn-ghost !py-3.5"><RotateCcw size={15} /> Nuova scansione</button>
          </div>
          <p className="text-[11px] text-gray-600 text-center">Lettura AI reale · Dati acquisiti, non ancora contabilizzati. Ogni numero resta tracciabile alla fonte.</p>
          <ResolveDialog doc={resolving} onClose={() => setResolving(null)}
            onResolved={async () => { const { data } = await api.get(`/documents/${doc.doc_id}`); setResult({ document: data }); }} />
        </div>
      )}
    </div>
  );
}
